import weatherLinkApi from '@/api/admin/weatherLinkApi'
import NoData from '@/components/common/NoData'
import SearchInput from '@/components/common/SearchInput'
import TableFooter from '@/components/common/Table/TableFooter'
import ButtonField from '@/components/form-control/ButtonField'
import ButtonFieldLight from '@/components/form-control/ButtonFieldLight'
import { RefreshIcon } from '@/components/icons'
import { flyInVariantsDashboard, queryKeys } from '@/constant/common'
import { useAuthContext } from '@/features/auth/context'
import { NotifyService } from '@/helpers'
import { SearchParams } from '@/types'
import { usePagination } from '@mui/lab'
import { CircularProgress, SelectChangeEvent, Stack } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { debounce } from 'lodash'
import { useState } from 'react'
import { ConfigLayout } from '../../components/common'
import ConfigHeader from '../../components/common/Header'
import { useConfigUI } from '../../context'
import useSearchSensor from '../../hooks/sensor/useSearchSensor'
import SensorsTable from './SensorsTable'
import classes from './style.module.scss'
import useTranslation from '@/hooks/useTranslation'

type Props = {}

const Sensors = (props: Props) => {
  const { t } = useTranslation()
  
  const MotionDiv = motion.div
  const { openSidebar, setSidebarView } = useConfigUI()
  const { interfaceId } = useAuthContext()
  const [showLoading, setShowLoading] = useState<boolean>(false)
  const [currentPage, setCurrentPage] = useState(1)
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useState<SearchParams>({
    PageSize: 10,
    PageIndex: 1,
  })
  const { data } = useSearchSensor(searchParams, Number(interfaceId))

  const handleEditSensor = (id: string) => {
    openSidebar()
    setSidebarView({ type: 'SENSOR_VIEW', id })
  }

  const handleSearch = debounce((event: SelectChangeEvent) => {
    const value = event.target.value.toString().trim()
    setCurrentPage(1)
    setSearchParams({
      ...searchParams,
      Keyword: value,
      PageIndex: 1,
    })
  }, 500)

  const { items } = usePagination({
    count: data?.totalPage,
    page: currentPage,
    onChange(_event: any, page: any) {
      setCurrentPage(page)
      setSearchParams({
        ...searchParams,
        PageIndex: page,
      })
    },
  })

  const handleChangeRowsPerPage = (e: SelectChangeEvent) => {
    const page = parseInt(e.target.value, 10)
    setCurrentPage(1)
    setSearchParams({
      ...searchParams,
      PageSize: page,
      PageIndex: 1,
    })
  }

  const handleResync = async () => {
    setShowLoading(true)
    await weatherLinkApi.getSync()
    queryClient
      .invalidateQueries([queryKeys.sensor])
      .then((res) => {
        NotifyService.success('Resync Sensors Success')
      })
      .catch((e) => NotifyService.error(e))
    setShowLoading(false)
  }

  return (
    <ConfigLayout
      title="Sites And Locations"
      header={<ConfigHeader subtitle="Data Config" title="Sensors" />}
    >
      <Stack className={classes.wrapper}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb="12px"
        >
          <MotionDiv
            initial="initialRight"
            animate="animate2"
            variants={flyInVariantsDashboard}
          >
            <SearchInput name="Search Sensors" onChange={handleSearch} />
          </MotionDiv>
          <MotionDiv
            initial="initialLeft"
            animate="animate2"
            variants={flyInVariantsDashboard}
          >
            {showLoading ? (
              <ButtonField
                startIcon={
                  <CircularProgress sx={{ color: 'white' }} size={14} />
                }
                style={{
                  height: '32px',
                  minWidth: '89px',
                  borderRadius: '5px',
                }}
                name="Syncing..."
              />
            ) : (
              <ButtonFieldLight
                startIcon={<RefreshIcon />}
                style={{
                  height: '32px',
                  minWidth: '89px',
                  borderRadius: '5px',
                }}
                name="Refresh"
                onClick={handleResync}
              />
            )}
          </MotionDiv>
        </Stack>

        <MotionDiv
          initial="initial"
          animate="animate"
          variants={flyInVariantsDashboard}
          className={classes.content}
        >
          {(!data || showLoading) && (
            <Stack
              sx={{
                position: 'absolute',
                transform: 'translate(-50%, -50%)',
                left: '50%',
                top: '35%',
              }}
            >
              <CircularProgress />
            </Stack>
          )}

          {data && !showLoading && data?.list.length > 0 && (
            <SensorsTable rows={data.list} onEditItem={handleEditSensor} />
          )}

          {data && data.totalCount === 0 && <NoData text={ t('noData') } />}
        </MotionDiv>
        <MotionDiv
          initial="initial"
          animate="animate"
          variants={flyInVariantsDashboard}
        >
          <Stack
            sx={{
              display: 'flex',
              bottom: '0',
              justifyContent: 'end',
              flexGrow: '1',
            }}
          >
            <TableFooter
              page={data?.page}
              totalCount={data?.totalCount}
              totalPage={data?.totalPage}
              rowsPerPage={data?.size ?? 0}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              items={items}
            />
          </Stack>
        </MotionDiv>
      </Stack>
    </ConfigLayout>
  )
}

export default Sensors
